import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { MsalProvider, useMsal } from "@azure/msal-react";
import DirectSearch from "./components/DirectSearch";
import HcpSearchApp from "./components/HcpSearchApp";
import HcpSearchTest from "./components/HcpSearchTest";
import HcpSearchItly from "./components/HcpSearchItly";
import HcpSearchFrance from "./components/HcpSearchFrance";
import { getMsalInstance, hasRouteAccess, defaultMsalInstance, italyMsalInstance } from "./authConfig";
import logo from "./logo.png";

// Storage key for remembering which SSO was used
const SSO_TYPE_KEY = "msal_sso_type";

// Component to handle authentication and routing logic
function AuthenticatedApp() {
    const location = useLocation();
    const navigate = useNavigate();
    const currentPath = location.pathname;

    const { instance, accounts, inProgress } = useMsal();
    const [hasRedirected, setHasRedirected] = useState(false);
    const [accessDenied, setAccessDenied] = useState(false);

    useEffect(() => {
        const checkLogin = async () => {
            try {
                const response = await instance.handleRedirectPromise();

                // If we just completed authentication, navigate to intended route
                if (response) {
                    const ssoType = sessionStorage.getItem(SSO_TYPE_KEY);
                    if (ssoType === "italy") {
                        navigate("/itly");
                    }
                    sessionStorage.removeItem(SSO_TYPE_KEY);
                    return;
                }

                // If not logged in
                if (!response && accounts.length === 0 && inProgress === "none") {
                    // Store which SSO type we're using before redirect
                    const isItalyRoute = currentPath.startsWith("/itly");
                    sessionStorage.setItem(SSO_TYPE_KEY, isItalyRoute ? "italy" : "default");

                    await instance.loginRedirect();
                    setHasRedirected(true);
                } else if (accounts.length > 0) {
                    // Check if user has access to current route
                    const access = hasRouteAccess(currentPath, accounts, instance);
                    setAccessDenied(!access);
                }
            } catch (error) {
                console.error("SSO Login Failed:", error);
            }
        };

        checkLogin();
    }, [instance, accounts, inProgress, currentPath, navigate]);

    // Check access whenever route changes
    useEffect(() => {
        if (accounts.length > 0) {
            const access = hasRouteAccess(currentPath, accounts, instance);
            setAccessDenied(!access);
        }
    }, [currentPath, accounts, instance]);

    if (inProgress !== "none") {
        return <p>Authenticating...</p>;
    }

    // Show access denied message if user is logged in but doesn't have access
    if (accessDenied && accounts.length > 0) {
        const isItalyRoute = currentPath.startsWith("/itly");
        const isItalyUser = instance === italyMsalInstance;

        return (
            <div style={{ padding: "40px", textAlign: "center" }}>
                <h1>Access Denied</h1>
                <p>
                    {isItalyUser
                        ? "Your account only has access to the Italy portal (/itly). Please navigate to /itly."
                        : "Your account does not have access to the Italy portal. Please contact your administrator."}
                </p>
                <button
                    onClick={() => instance.logoutRedirect({
                        postLogoutRedirectUri: window.location.origin,
                    })}
                    style={{ marginTop: "20px", padding: "10px 20px" }}
                >
                    Logout
                </button>
            </div>
        );
    }

    if (accounts.length > 0) {
        return (
            <div className="App">
                <header className="app-header">
                    <img
                        src={logo}
                        alt="Vector Health Logo"
                        className="logo"
                        style={{ marginLeft: "41%", marginTop: "15px", height: "70px" }}
                    />
                    <div className="actions">
                        <button
                            onClick={() =>
                                instance.logoutRedirect({
                                    postLogoutRedirectUri: window.location.origin,
                                })
                            }
                            style={{ position: "absolute", top: "20px", right: "20px" }}
                        >
                            Logout
                        </button>
                    </div>
                </header>
                <main>
                    <Routes>
                        <Route path="/" element={<DirectSearch />} />
                        <Route path="/by_maaz" element={<HcpSearchApp />} />
                        <Route path="/test" element={<HcpSearchTest />} />
                        <Route path="/itly" element={<HcpSearchItly />} />
                        <Route path="/france" element={<HcpSearchFrance />} />
                    </Routes>
                </main>
            </div>
        );
    } else if (hasRedirected) {
        return <p>Redirecting...</p>;
    } else {
        return <p>Please Login</p>;
    }
}

// Wrapper to provide the correct MSAL instance
function MsalWrapper() {
    const [msalInstance, setMsalInstance] = useState(null);

    useEffect(() => {
        // Determine which MSAL instance to use
        const determineInstance = () => {
            // Check if we're returning from a redirect
            const ssoType = sessionStorage.getItem(SSO_TYPE_KEY);

            if (ssoType === "italy") {
                return italyMsalInstance;
            } else if (ssoType === "default") {
                return defaultMsalInstance;
            }

            // No redirect in progress, check current path
            const currentPath = window.location.pathname;
            return getMsalInstance(currentPath);
        };

        const instance = determineInstance();
        setMsalInstance(instance);
    }, []);

    if (!msalInstance) {
        return <p>Loading...</p>;
    }

    return (
        <MsalProvider instance={msalInstance}>
            <AuthenticatedApp />
        </MsalProvider>
    );
}

function App() {
    return (
        <Router>
            <MsalWrapper />
        </Router>
    );
}

export default App;

